import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RecircBoxSmallList } from "@bloomberg/media-ui-core/client";
import {
  type PersonalizationAsset,
  RecommendationAlgorithm,
  RecommendationType,
  usePersonalization,
} from "@foundation/personalization";
import { useIntl } from "react-intl";
import { UsePersonalizationProps } from "@foundation/personalization/lib/usePersonalization/usePersonalization";
import type { DaperStorySearchParams } from "@foundation/intercom-client";
import styles from "./personalizedModule.module.css";
import { fetchStoriesWithImageFromDaper } from "~api/fetchStoriesWithImagesFromDaper";
import { ArticleRecircStory } from "~types/types";
import { useTranslateMessage } from "~utils/i18n";

/**
 * Transform the foundation personalization stories to
 * the media-ui-core stories
 *
 * @param {PersonalizationAsset[]} recommendedStories
 */
function transformStories(recommendedStories: any[]) {
  return recommendedStories.map((story) => ({
    headline: story.headline,
    id: story.assetID ?? story.id,
    image: story.image ?? {
      src: story.thumbnailUrl || "",
    },
    publishedAt: story.publishedAt,
    url: story.url,
  }));
}

const personalizationProps = {
  autofillData: {
    type: RecommendationType.Personalized,
    args: {
      algorithm: RecommendationAlgorithm.RR,
      limit: "6",
      maxAge: "604800000",
      thumbnailRequired: "true",
      resourceTypes: "Story",
    },
  },
};

const JA_DAPER_FETCH_CONFIG = {
  limit: 5,
  "attachments.image.placement": ["thumbnail"],
  "tags.niCodes": ["JKTOP"],
} as DaperStorySearchParams;

export function PersonalizedRecircBoxSmallList({
  ticker,
}: {
  ticker?: string;
}) {
  const { locale } = useIntl();
  const translateMessage = useTranslateMessage();
  const [recommendedStoriesByLocale, setRecommendedStoriesByLocale] = useState<
    PersonalizationAsset[] | ArticleRecircStory[]
  >([]);

  const tailoredPersonalizationProps = useMemo(
    () =>
      ({
        autofillData: {
          ...personalizationProps?.autofillData,
          args: {
            ...personalizationProps.autofillData?.args,
            ...(ticker ? { currentTags: `Company|${ticker}` } : undefined),
          },
        },
      } as UsePersonalizationProps<PersonalizationAsset>),
    [ticker]
  );

  const { recommendations: recommendedStories, error: personalizationError } =
    usePersonalization(tailoredPersonalizationProps);

  // eslint-disable-next-line require-await
  const getRecommendationByLocale = useCallback(async () => {
    if (locale === "en") {
      return recommendedStories;
    }

    return fetchStoriesWithImageFromDaper(JA_DAPER_FETCH_CONFIG);
  }, [locale, recommendedStories]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      const stories = await getRecommendationByLocale();

      setRecommendedStoriesByLocale(stories);
    };

    fetchRecommendations();
  }, [getRecommendationByLocale]);

  if (personalizationError) {
    console.error(
      "Error fetching personalization data: ",
      personalizationError
    );
  }

  return (
    <RecircBoxSmallList
      className={styles.moreFromBloomberg}
      locale={locale}
      stories={transformStories(recommendedStoriesByLocale)}
      title={translateMessage({
        id: "moreFromBloomberg",
        defaultMessage: "More from Bloomberg",
      })}
    />
  );
}

export default memo(PersonalizedRecircBoxSmallList);
